import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  HStack,
  IconButton,
  Input,
  Link,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useRole } from '@/hooks/useRole';
import Layout from '@/features/common/Layout.jsx';
import { Select } from 'chakra-react-select';
import { chakraStyles } from '@/features/common/select/styles.js';
import { selectEntityTypeList, selectEventSourceList } from '@/features/enum/enumSlice.js';
import { FiLink2, FiSearch } from 'react-icons/fi';
import { hostname, nucleusApi, useGetEntityAuditLogsQuery, useLazyGetEntityQuery } from '@/app/services/nucleus.js';
import { AUDIT_LOG } from '@/utils/constants.js';
import { isArrayEmpty, isArrayValue, isRealValue } from '@/utils/helpers.js';
import AlertComponent from '@/features/common/AlertComponent.jsx';
import { useLocation, useSearchParams } from 'react-router-dom';
import ReactDiffViewer from 'react-diff-viewer-continued';
import ModalComponent from '@/features/common/ModalComponent.jsx';
import { entityResultParser } from '@/features/entity/helpers.jsx';
import LargePayloadBlock, { isOversizedPayload } from '@/features/troubleshooting/LargePayloadBlock.jsx';
import DataTable from '@/features/common/table/DataTable.jsx';
import SSDataTable from '@/features/common/table/SSDataTable.jsx';
import { usePagination } from '@/hooks/usePagination.js';
import { useServerSideSorting } from '@/hooks/useServerSideSorting.js';
import { GoDiff } from 'react-icons/go';
import { JsonEditor, githubLightTheme } from 'json-edit-react';
import ActorBadge from '@/features/common/ActorBadge.jsx';

const AUDIT_LOGS_TAB_INDEX = 1;

const ActorCell = ({ row }) => {
  // Candidate wins over user, matching how Nucleus attributes the change.
  const actorId = row.updated_by_candidate_id ?? row.updated_by_id;

  if (!actorId) {
    return <ActorBadge isSystem={true} />;
  }

  if (!row.actor) {
    return (
      <Text fontSize={'xs'} fontFamily={'Fira Code,monospace'} wordBreak={'break-all'}>
        {actorId}
      </Text>
    );
  }

  return <ActorBadge actor={row.actor} />;
};

const EntityLookup = () => {
  useRole('support');
  const latestLookup = useRef(null);
  const formWroteUrl = useRef(false);
  const location = useLocation();
  const compareValuesModal = useDisclosure();
  const columnHelper = createColumnHelper();
  const [searchParams, setSearchParams] = useSearchParams();
  const [source, setSource] = useState(null);
  const [entity, setEntity] = useState(null);
  const [systemId, setSystemId] = useState('');
  const [jsonSearch, setJsonSearch] = useState('');
  const [jsonSearchFilter, setJsonSearchFilter] = useState({ value: 'all', label: 'All' });
  const [entityResult, setEntityResult] = useState(null);
  // The entity the audit logs belong to, captured at search time so editing the form does not refetch them.
  const [lookup, setLookup] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [rowId, setRowId] = useState(null);
  const [auditLogPageCount, setAuditLogPageCount] = useState();

  const dispatch = useDispatch();
  const sourceList = useSelector(selectEventSourceList);
  const entityTypeList = useSelector(selectEntityTypeList);

  const [getEntity, { isFetching: isEntityFetching, isError: isEntityError, error: entityError }] = useLazyGetEntityQuery();

  const { limit, offset, pagination: auditLogPagination, onPaginationChange: onAuditLogPaginationChange } = usePagination();
  const {
    sorting: auditLogSorting,
    onSortingChange: onAuditLogSortingChange,
    sortByField: auditLogSortBy,
  } = useServerSideSorting([{ id: 'occurred_at', desc: true }]);

  const {
    data: auditLogPage,
    isFetching: isAuditLogFetching,
    isError: isAuditLogError,
    error: auditLogError,
  } = useGetEntityAuditLogsQuery(
    { ...lookup, limit, offset, sortBy: auditLogSortBy, expand: 'actor' },
    { skip: !lookup || tabIndex !== AUDIT_LOGS_TAB_INDEX },
  );

  const auditLogs = useMemo(() => auditLogPage?.items ?? [], [auditLogPage]);

  useEffect(() => {
    if (isRealValue(auditLogPage?.total)) {
      setAuditLogPageCount(Math.ceil(auditLogPage.total / limit));
    }
  }, [auditLogPage?.total, limit]);

  const systemIds = useMemo(() => {
    if (!entityResult?.system_ids) {
      return;
    }
    return entityResult.system_ids.map((item) => {
      return {
        ...item,
        is_active: item.is_active ? 'Yes' : 'No',
        system_id: item.system_id || 'null',
      };
    });
  }, [entityResult]);

  const diffValues = useMemo(() => {
    if (!isArrayValue(auditLogs)) {
      return [];
    }
    return auditLogs.map((item) => ({
      id: item.id,
      old: item.old_value,
      new: item.new_value,
    }));
  }, [auditLogs]);

  const selectedValues = useMemo(() => (rowId ? diffValues.find((item) => item.id === rowId) : null), [rowId, diffValues]);

  const entityJson = useMemo(() => (entityResult ? JSON.stringify(entityResult, null, 2) : ''), [entityResult]);
  const isEntityOversized = isOversizedPayload(entityJson);

  const handleEditBtnClick = (id) => {
    setRowId(id);
    compareValuesModal.onOpen();
  };

  const handleOldNewValues = (value, title, id) => {
    const textProps = {
      fontSize: 'sm',
      fontFamily: 'Fira Code,monospace',
    };

    // Handle empty arrays and null values
    if (isArrayEmpty(value) || value === null || value === 'null') {
      const displayValue = isArrayEmpty(value) ? '[]' : 'null';
      return <Text {...textProps}>{displayValue}</Text>;
    }

    // Non-empty values
    const stringValue = JSON.stringify(value);

    return (
      <Flex alignItems={'center'}>
        <Text maxWidth={'120px'} isTruncated={true} {...textProps}>
          {stringValue}
        </Text>
      </Flex>
    );
  };

  // Nucleus only sorts audit logs by `occurred_at`, so no other header offers a toggle.
  const columns = useMemo(
    () => [
      columnHelper.accessor('occurred_at', {
        cell: (info) => (info.getValue() ? new Date(info.getValue()).toLocaleString() : null),
        header: 'Occurred At',
      }),
      columnHelper.accessor('field', {
        cell: (info) => info.getValue(),
        header: 'Field',
        disableSortBy: true,
      }),
      columnHelper.accessor('old_value', {
        cell: (info) => handleOldNewValues(info.getValue(), 'old', info.row.original.id),
        header: 'Old Value',
        disableSortBy: true,
      }),
      columnHelper.accessor('new_value', {
        cell: (info) => handleOldNewValues(info.getValue(), 'new', info.row.original.id),
        header: 'New Value',
        disableSortBy: true,
      }),
      columnHelper.accessor('', {
        cell: (info) => {
          return (
            <Box p="0" as="span">
              <IconButton aria-label="compare" variant="outline" icon={<GoDiff />} onClick={(e) => handleEditBtnClick(info.row.original.id)} />
            </Box>
          );
        },
        header: 'Compare',
        disableSortBy: true,
      }),
      columnHelper.accessor('system', {
        cell: (info) => info.getValue(),
        header: 'System',
        disableSortBy: true,
      }),
      columnHelper.accessor('actor', {
        cell: (info) => <ActorCell row={info.row.original} />,
        header: 'Actor',
        disableSortBy: true,
      }),
    ],
    [setRowId],
  );

  const systemIdColumns = useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: (info) => info.getValue(),
        header: 'Name',
      }),
      columnHelper.accessor('is_active', {
        cell: (info) => {
          const val = info.getValue();
          return <Badge colorScheme={val === 'Yes' ? 'green' : 'red'}>{val}</Badge>;
        },
        header: 'Active',
      }),
      columnHelper.accessor('system_id', {
        cell: (info) => info.getValue(),
        header: 'System ID',
      }),
      columnHelper.accessor('', {
        cell: (info) => {
          return (
            <Box p="0" as="span">
              <Link href={`${hostname}/redirects/jump/${entity.value}/${entityResult?.id}/${info.row.original.name}`}>
                <IconButton aria-label="open-entity" variant="outline" icon={<FiLink2 />} />
              </Link>
            </Box>
          );
        },
        header: 'Links',
      }),
    ],
    [setRowId, source, entityResult],
  );

  const applyEntityResult = (result, params) => {
    setEntityResult(result);
    setLookup(params);
    onAuditLogPaginationChange({ pageSize: limit, pageIndex: 0 });
    // Searching the same entity twice leaves the audit query arguments identical, so without this
    // the tab would answer a fresh search from the page it already held.
    dispatch(nucleusApi.util.invalidateTags([AUDIT_LOG]));
  };

  const lookupEntity = async (params) => {
    latestLookup.current = params;
    try {
      const result = await getEntity(params).unwrap();
      // Stepping back and forward quickly can resolve lookups out of order.
      if (latestLookup.current === params && isRealValue(result)) {
        applyEntityResult(result, params);
      }
    } catch (error) {
      console.log('Error fetching entity', error);
    }
  };

  // Every navigation the form did not make (first load, a followed link, back or forward) is a new lookup.
  useEffect(() => {
    if (formWroteUrl.current) {
      formWroteUrl.current = false;
      return;
    }

    const sourceValue = searchParams.get('source');
    const entityValue = searchParams.get('entity');
    const idValue = searchParams.get('id') ?? '';

    setSource(sourceValue ? { label: sourceValue, value: sourceValue } : null);
    setEntity(entityValue ? { label: entityValue, value: entityValue } : null);
    setSystemId(idValue);

    if (sourceValue && entityValue && idValue) {
      window.scrollTo(0, 0);
      lookupEntity({ entity: entityValue, source: sourceValue, systemId: idValue });
    } else {
      latestLookup.current = null;
      setEntityResult(null);
      setLookup(null);
    }
  }, [location.key]);

  const writeFormToUrl = (updateParams) => {
    formWroteUrl.current = true;
    setSearchParams(updateParams, { replace: true });
  };

  // Generic handler for select components (source and entity type)
  const handleSelectChange =
    (paramName, setValue) =>
    (value, { action }) => {
      setValue(value);

      writeFormToUrl((params) => {
        const newParams = new URLSearchParams(params);

        if (action === 'select-option' && value?.value) {
          newParams.set(paramName, value.value);
        } else if (action === 'clear') {
          newParams.delete(paramName);
        }

        return newParams;
      });
    };

  // Handler for input field (system ID)
  const handleInputChange = (paramName, setValue) => (event) => {
    const value = event.target.value;
    setValue(value);

    writeFormToUrl((params) => {
      const newParams = new URLSearchParams(params);

      if (value === '') {
        newParams.delete(paramName);
      } else {
        newParams.set(paramName, value);
      }

      return newParams;
    });
  };

  const handleSourceChange = handleSelectChange('source', setSource);
  const handleEntityTypeChange = handleSelectChange('entity', setEntity);
  const handleSystemIdChange = handleInputChange('id', setSystemId);

  const handleJsonSearchChange = (event) => {
    setJsonSearch(event.target.value);
  };

  const handleJsonFilterChange = (value) => {
    setJsonSearchFilter(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await lookupEntity({ entity: entity?.value, source: source?.value, systemId });
  };

  const handleTabsChange = (index) => {
    setTabIndex(index);
  };

  return (
    <Box data-testid="pact-margin-rules">
      <Layout pageTitle={'Pact'}>
        <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
          Entity Lookup
        </Heading>
        <Box mt="6" p={0} pos={'relative'}>
          {isEntityFetching && (
            <Flex
              alignItems="center"
              justifyContent="center"
              pos={'absolute'}
              bg={'rgba(255,255,255,0.6)'}
              height={'100%'}
              zIndex={1200}
              width="100%"
            >
              <Spinner color="brand.900" size="lg" />
            </Flex>
          )}
          <form id="add_facility" onSubmit={handleSubmit}>
            <VStack spacing={4} justifyContent={'space-between'} maxWidth={'60%'}>
              <FormControl id="source">
                <Box>
                  <Select
                    id="source-select"
                    aria-label="source-select"
                    chakraStyles={chakraStyles}
                    focusBorderColor="brand.700"
                    options={sourceList}
                    onChange={handleSourceChange}
                    isClearable
                    isSearchable
                    value={source}
                    placeholder="Select a source"
                  />
                  <FormErrorMessage aria-label="source-error">Error setting source</FormErrorMessage>
                </Box>
              </FormControl>
              <FormControl id="entity-type">
                <Box>
                  <Select
                    id="entity-type-select"
                    aria-label="entity-type-select"
                    chakraStyles={chakraStyles}
                    focusBorderColor="brand.700"
                    options={entityTypeList}
                    onChange={handleEntityTypeChange}
                    isClearable
                    isSearchable
                    value={entity}
                    placeholder="Select an entity type"
                  />
                  <FormErrorMessage aria-label="source-error">Error setting source</FormErrorMessage>
                </Box>
              </FormControl>
              <FormControl id="system-id">
                <Input
                  id="system-id-input"
                  aria-label="system-id-input"
                  focusBorderColor={'brand.700'}
                  type="text"
                  value={systemId}
                  onChange={handleSystemIdChange}
                  placeholder="System ID"
                />
                <FormErrorMessage aria-label="system-id-error">Error setting system ID</FormErrorMessage>
              </FormControl>
              <FormControl id="system-id" py={6}>
                <Button leftIcon={<FiSearch />} variant="purple" type="submit">
                  Search
                </Button>
              </FormControl>
            </VStack>
          </form>
          <Divider />
          {isEntityError ? (
            <AlertComponent mb={6} status="error" title="Error Fetching Entity" description={entityError?.data?.detail} />
          ) : (
            <Box mt={4}>
              {entityResult && (
                <>
                  <Heading as="h4" size="md" fontWeight="bold" color="brand.900" mb={4}>
                    Results
                  </Heading>
                  <Tabs index={tabIndex} mt={6} colorScheme="cyan" isFitted onChange={handleTabsChange} isLazy>
                    <TabList>
                      <Tab>Entity Lookup</Tab>
                      <Tab>Audit Logs</Tab>
                      <Tab>System IDs</Tab>
                      <Tab>Inspect</Tab>
                    </TabList>

                    <TabPanels>
                      <TabPanel>
                        <VStack spacing={4} justifyContent={'space-between'} maxWidth={'90%'}>
                          {isRealValue(entityResult) && entityResultParser(entityResult)}
                        </VStack>
                      </TabPanel>
                      <TabPanel>
                        <Box>
                          {isAuditLogError ? (
                            <AlertComponent status="error" title="Error Fetching Audit Logs" description={auditLogError?.data?.detail} />
                          ) : !isAuditLogFetching && auditLogPage?.total === 0 ? (
                            <Box p={3} bg={'gray.50'} width={'100%'} borderRadius={6} mt={4}>
                              {'No Audit Logs Found'}
                            </Box>
                          ) : (
                            <Box p={3} bg={'gray.50'} width={'100%'} borderRadius={9} mt={4}>
                              <SSDataTable
                                loading={isAuditLogFetching}
                                showScrollbar={true}
                                data={auditLogs}
                                total={auditLogPage?.total ?? 0}
                                columns={columns}
                                pagination={auditLogPagination}
                                pageCount={auditLogPageCount}
                                onPaginationChange={onAuditLogPaginationChange}
                                sorting={auditLogSorting}
                                onSortingChange={onAuditLogSortingChange}
                              />
                            </Box>
                          )}
                        </Box>
                      </TabPanel>
                      <TabPanel>
                        <Box>
                          {isArrayValue(systemIds) ? (
                            <Box p={3} bg={'gray.50'} width={'100%'} borderRadius={9} mt={4}>
                              <DataTable showScrollbar={true} data={systemIds} columns={systemIdColumns} />
                            </Box>
                          ) : (
                            <Box p={3} bg={'gray.50'} width={'100%'} borderRadius={6} mt={4}>
                              {'No System IDs Found'}
                            </Box>
                          )}
                        </Box>
                      </TabPanel>
                      <TabPanel>
                        <Box width={'90%'} p={4}>
                          {isEntityOversized ? (
                            <LargePayloadBlock text={entityJson} label="entity" />
                          ) : (
                            <>
                              <HStack gap={4}>
                                <FormControl id="system-id">
                                  <Input
                                    id="search-json-input"
                                    aria-label="search-json-input"
                                    focusBorderColor={'brand.700'}
                                    type="text"
                                    value={jsonSearch}
                                    onChange={handleJsonSearchChange}
                                    placeholder="Search"
                                  />
                                  <FormErrorMessage aria-label="system-id-error">Error searching JSON</FormErrorMessage>
                                </FormControl>
                                <FormControl id="json-filter">
                                  <Box>
                                    <Select
                                      id="json-filter"
                                      aria-label="json-filter"
                                      chakraStyles={chakraStyles}
                                      focusBorderColor="brand.700"
                                      options={[
                                        { value: 'key', label: 'Key' },
                                        { value: 'value', label: 'Value' },
                                        { value: 'all', label: 'all' },
                                      ]}
                                      onChange={handleJsonFilterChange}
                                      isClearable
                                      isSearchable
                                      value={jsonSearchFilter}
                                      placeholder="Select a filter"
                                    />
                                    <FormErrorMessage aria-label="source-error">Error setting JSON filter</FormErrorMessage>
                                  </Box>
                                </FormControl>
                              </HStack>
                              <div
                                style={{
                                  width: '100%',
                                  paddingTop: '1rem',
                                }}
                              >
                                <JsonEditor
                                  restrictEdit={true}
                                  restrictAdd={true}
                                  restrictDelete={true}
                                  data={entityResult}
                                  searchText={jsonSearch}
                                  searchFilter={jsonSearchFilter?.value ?? 'all'}
                                  theme={{
                                    displayName: 'Triage',
                                    fragments: { edit: 'rgb(42, 161, 152)' },
                                    styles: {
                                      ...githubLightTheme.styles,
                                      container: {
                                        fontFamily: 'Fira Code, monospace',
                                        fontSize: '14px',
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </>
                          )}
                        </Box>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </>
              )}
            </Box>
          )}
        </Box>
        <ModalComponent
          size="lg"
          title={'Compare Values'}
          secondaryText="Close"
          onOpen={compareValuesModal.isOpen}
          isOpen={compareValuesModal.isOpen}
          onClose={compareValuesModal.onClose}
          isError={false}
        >
          <div
            style={{
              fontFamily: 'Fira Code, monospace',
              fontSize: '14px',
              width: '100%',
            }}
          >
            <HStack gap={2} justify={'space-between'}>
              <ReactDiffViewer
                leftTitle={'Old Value'}
                rightTitle={'New Value'}
                oldValue={selectedValues?.old ? JSON.stringify(selectedValues?.old, null, 2) : 'null'}
                newValue={selectedValues?.new ? JSON.stringify(selectedValues?.new, null, 2) : 'null'}
                splitView={true}
              />
            </HStack>
          </div>
        </ModalComponent>
      </Layout>
    </Box>
  );
};

export default EntityLookup;
